import React, { FC, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button as MuiButton,
  Divider,
  IconButton,
  InputBase,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Add, Bolt, Delete as DeleteIcon, Remove } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';

import { UserRole } from '../../types/user.types';
import { SlideWithOptions } from '../../api-http/presentations';
import { VideoModeEnum } from '../../components/canvas-editor-v2/lib/FabricJs/utils/handleVideo';

import { SortableList } from '../../components';
import { useIsUserRole } from '../../util/useIsUserRole';
import { AnimationEnum, TRANSITIONS } from '../../components/preview/constants';
import { CanvasPreviewV2 } from '../../components/canvas-preview-v2/CanvasPreviewV2';

const Container = styled.div`
  display: flex;
  width: 100%;
`;

const Button = muiStyled(MuiButton)(({}) => ({
  minWidth: 168,
  height: 38,
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.01em',
  textTransform: 'none',
}));

interface PreviewListProp {
  slides: SlideWithOptions[];
  onSlideRemove: (uniqId: string) => void;
  onSlideOrderChange: (uniqId: any) => void;
  setSlides: (uniqId: any) => void;
  durationOnChange: (data: { duration: number; uniqId: string }) => void;
  transitionOnChange: (data: { animation: string; uniqId: string }) => void;
  initialTransitions: Array<string>;
  refetchPresentation: () => void;
  isPublished: boolean;
  updatedKvs: { [key: string]: string };
}

interface SlideCardProp extends Omit<PreviewListProp, 'slides'> {
  slide: SlideWithOptions;
  isViewOnly: boolean;
  uniqId: string;
  slideCount: number;
}

const SlideCard = ({
  slide,
  onSlideRemove,
  durationOnChange,
  transitionOnChange,
  uniqId,
  isPublished,
  updatedKvs,
  slideCount,
}: SlideCardProp) => {
  const { slideData, duration: slideDuration, animation } = slide;

  const [duration, setDuration] = useState(slideDuration / 1000);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!slideData) {
    return null;
  }

  const { projectData } = slideData;
  for (const object of projectData?.objects || []) {
    if (object?.data?.type === 'video') {
      object.src = '';
    } else if (object?.data?.type === 'kvs' && updatedKvs) {
      const kvsId = object?.data?.kvsId;
      if (updatedKvs[kvsId]) {
        object.text = updatedKvs[kvsId];
      }
    }
  }

  const isSingleSlide = slideCount === 1;

  return (
    <>
      <Box
        sx={{
          overflow: 'hidden',
          width: { xs: '100%', sm: 280, lg: 320 },
          aspectRatio: '16 / 9',
          flexShrink: 0,
          bgcolor: '#f1f5f9',
          border: '1px solid rgba(15, 23, 42, 0.10)',
          borderRadius: 2,
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.35)',
          '& canvas': { maxWidth: '100%', height: 'auto !important' },
        }}
      >
        <CanvasPreviewV2
          json={projectData}
          canvasId={uuidv4()}
          style={{ display: 'flex', justifyContent: 'center' }}
          mode={VideoModeEnum.Thumbnail}
        />
      </Box>

      <Box
        sx={{
          minWidth: 180,
          flex: 1,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2.5,
        }}
      >
        <Link
          href={`/editor-v2/${slide.slideId}?goBackUrl=${window.location.pathname}`}
          variant="body2"
          underline="hover"
          sx={{ width: 'fit-content' }}
        >
          <Typography
            variant="h6"
            sx={{ color: '#0f172a', fontWeight: 700, letterSpacing: '-0.01em' }}
          >
            {slideData.title}
          </Typography>
        </Link>
        {!isSingleSlide && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1.5,
                color: '#475569',
                fontWeight: 500,
              }}
            >
              Duration (in seconds)
            </Typography>
            <Paper
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                width: 'fit-content',
                overflow: 'hidden',
                border: '1px solid #cbd5e1',
                borderRadius: 2,
                boxShadow: 'none',
              }}
            >
              <IconButton
                type="button"
                size="small"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 0,
                  color: '#64748b',
                }}
                onClick={() => {
                  durationOnChange({ duration: (duration - 1) * 1000, uniqId });
                  setDuration(duration - 1);
                }}
                disabled={isPublished || duration - 1 === 0}
              >
                <Remove fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 38,
                  borderLeft: '1px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0',
                }}
              >
                <InputBase
                  sx={{
                    width: 42,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    '& input[type="number"]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                  }}
                  value={duration}
                  inputProps={{ min: 0, style: { textAlign: 'center' } }}
                  type="number"
                  onChange={(e) => {
                    setDuration(Number(e.target.value));
                  }}
                  onBlur={() => {
                    durationOnChange({ duration: duration * 1000, uniqId });
                  }}
                  disabled={isPublished}
                />
              </Box>
              <IconButton
                type="button"
                size="small"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 0,
                  color: '#64748b',
                }}
                onClick={() => {
                  durationOnChange({ duration: (duration + 1) * 1000, uniqId });
                  setDuration(duration + 1);
                }}
                disabled={isPublished}
              >
                <Add fontSize="small" />
              </IconButton>
            </Paper>
          </Box>
        )}
      </Box>
      <Stack
        sx={{
          minWidth: { xs: '100%', md: 180 },
          minHeight: { md: 160 },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          alignItems: { xs: 'center', md: 'flex-end' },
          justifyContent: 'space-between',
          gap: 3,
          pt: { xs: 3, md: 0 },
          borderTop: { xs: '1px solid #f1f5f9', md: 0 },
        }}
      >
        <Button
          id="transition-button"
          variant="outlined"
          startIcon={<Bolt />}
          onClick={handleClick}
          disabled={isPublished}
          sx={{
            bgcolor: '#f8fcff',
            borderColor: '#62c7f8',
            color: '#087fbd',
            '&:hover': {
              bgcolor: '#edf8fe',
              borderColor: '#35b5f1',
            },
          }}
        >
          {animation} {animation !== AnimationEnum.none && 'Transition'}
        </Button>
        <Menu
          id="transition-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'transition-button',
          }}
        >
          {TRANSITIONS.map((transition, i) => (
            <div key={i}>
              {i !== 0 && <Divider />}
              <MenuItem
                onClick={() => {
                  handleClose();
                  transitionOnChange({ animation: transition, uniqId });
                }}
                sx={{ width: 200 }}
              >
                {transition}
              </MenuItem>
            </div>
          ))}
        </Menu>
        <Button
          onClick={() => {
            onSlideRemove(uniqId);
          }}
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={isPublished}
          sx={{
            bgcolor: '#fffafb',
            borderColor: '#ffbec7',
            color: '#ef2345',
            '&:hover': {
              bgcolor: '#fff1f3',
              borderColor: '#ff9fad',
            },
          }}
        >
          Delete
        </Button>
      </Stack>
    </>
  );
};

export const PreviewList: FC<PreviewListProp> = ({
  slides,
  refetchPresentation,
  isPublished,
  ...props
}) => {
  const isViewOnly = useIsUserRole([UserRole.user, UserRole.dataEditor]);

  return (
    <Container>
      <SortableList
        items={slides}
        onChange={(data) => {
          props.setSlides(data);
          const slides = [];
          for (const slide of data) {
            const { slideId } = slide;
            slides.push({
              ...slide,
              slideData: slideId,
            });
          }
          props.onSlideOrderChange(slides);
        }}
        renderItem={(item) => {
          const slideNumber = slides.findIndex(({ id }) => id === item.id) + 1;

          return (
            <SortableList.Item id={item.id}>
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ flexShrink: 0 }}
              >
                <Box
                  aria-label={`Slide ${slideNumber}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: '#eef2ff',
                    color: '#4338ca',
                    border: '1px solid #c7d2fe',
                    fontSize: 12,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  {slideNumber}
                </Box>
                {!isPublished && slides.length > 1 && (
                  <SortableList.DragHandle />
                )}
              </Stack>
              <SlideCard
                key={item.id}
                {...props}
                slide={item}
                uniqId={item.id}
                isViewOnly={Boolean(isViewOnly)}
                refetchPresentation={refetchPresentation}
                isPublished={isPublished}
                slideCount={slides.length}
              />
            </SortableList.Item>
          );
        }}
      />
    </Container>
  );
};
